import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface TwilioPhoneNumber {
  phoneNumber: string;
  friendlyName: string;
  capabilities: {
    voice: boolean;
    SMS: boolean;
    MMS: boolean;
  };
}

export interface PhoneNumberOption {
  value: string;
  label: string;
  assignedTo: string | null;
  assignedToName: string | null;
  disabled: boolean;
}

export function useTwilioPhoneNumbers(currentUserId?: string) {
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumberOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    loadPhoneNumbers();
  }, [currentUserId]);

  const loadPhoneNumbers = async () => {
    try {
      setLoading(true);

      // Busca números do Twilio
      const { data: twilioNumbers, error: twilioError } = await supabase.functions.invoke('twilio-list-numbers');
      
      if (twilioError) {
        console.error('Error fetching Twilio numbers:', twilioError);
        // Se falhar, continua com lista vazia
      }

      // Busca todos os usuários e seus números atrelados
      const { data: users, error: usersError } = await supabase
        .from('app_users')
        .select('id, twilio_phone_number, display_name, first_name, last_name')
        .not('twilio_phone_number', 'is', null);

      if (usersError) throw usersError;

      // Cria um mapa de números para usuários
      const numberToUserMap = new Map();
      users?.forEach(user => {
        if (user.twilio_phone_number) {
          const userName = user.display_name || 
            `${user.first_name || ''} ${user.last_name || ''}`.trim() || 
            'Unknown User';
          numberToUserMap.set(user.twilio_phone_number, {
            id: user.id,
            name: userName
          });
        }
      });

      // Processa os números do Twilio
      const numbers = twilioNumbers?.phoneNumbers || [];
      const options: PhoneNumberOption[] = numbers.map((num: TwilioPhoneNumber) => {
        const assignedUser = numberToUserMap.get(num.phoneNumber);
        const isAssignedToCurrentUser = assignedUser?.id === currentUserId;
        
        return {
          value: num.phoneNumber,
          label: assignedUser 
            ? `${num.phoneNumber} - Assigned to ${assignedUser.name}`
            : num.phoneNumber,
          assignedTo: assignedUser?.id || null,
          assignedToName: assignedUser?.name || null,
          disabled: assignedUser && !isAssignedToCurrentUser
        };
      });

      // Add "None" option at the beginning
      options.unshift({
        value: 'none',
        label: 'No number assigned',
        assignedTo: null,
        assignedToName: null,
        disabled: false
      });

      setPhoneNumbers(options);
      setError(null);
    } catch (err) {
      console.error('Error loading phone numbers:', err);
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  };

  return {
    phoneNumbers,
    loading,
    error,
    reload: loadPhoneNumbers
  };
}
